import {Injectable, OnInit} from '@angular/core'
import {LocalStorageService} from './utils/local-storage.service'
import {LbHttpService} from './lb-http.service'
import {ConfigService} from './config.service'
import {catchError, first, retryWhen, switchMap, timeout} from 'rxjs/operators'
import { Observable, of, throwError } from 'rxjs'
import {ErrorService} from './utils/error.service'
import {HttpClient} from '@angular/common/http'
import {EndpointService} from './resources/endpoint.service'
import { PwaService } from './utils/pwa.service'

@Injectable({
  providedIn: 'root'
})
export class ApplicationsService {

  private static CURRENT_APPLICATION_KEY = 'currentApplication'

  constructor(
      private http: HttpClient,
      public pwaService: PwaService,
      public localStorage: LocalStorageService,
      public lbHttpService: LbHttpService,
      public configService: ConfigService,
      public errorService: ErrorService,
      public endpointService: EndpointService
  ) {
    errorService.setDisplayError( this.getShowDebug() )
  }

  public getCurrentApplication() {
    return this.localStorage.getStorageValue( ApplicationsService.CURRENT_APPLICATION_KEY )
  }

  public deleteCurrentApplication() {
    return this.localStorage.setStorageValue( ApplicationsService.CURRENT_APPLICATION_KEY, null )
  }

  getShowDebug() {
    let showDebug = false
    const application = this.getCurrentApplication()
    if (
        application
        && application.config
        && typeof( application.config.showDebug ) !== 'undefined'
    ) {
      showDebug = application.config.showDebug
    }
    return showDebug
  }

  getTheme() {
    let theme = null
    const application = this.getCurrentApplication()
    if (
        application
        && typeof( application.theme ) !== 'undefined'
    ) {
      theme = application.theme
    }
    return theme
  }

  loadApplication ( applicationId ) {
    console.log( 'loadApplication' )
    if ( applicationId ) {
      let application

      return this.httpGetApplicationById( applicationId ).pipe(
        switchMap( ( applicationRes: any ) => {
          application = applicationRes
          this.localStorage.setApplicationId( applicationId )
          this.localStorage.setStorageValue(  ApplicationsService.CURRENT_APPLICATION_KEY , application )
          this.localStorage.setTheme( this.getTheme() )
          this.errorService.setDisplayError( applicationId )
          this.pwaService.changeManifest( applicationId )
          if (application && application.config && typeof application.config.logLevel !== 'undefined') {
            this.errorService.setLogLevel( application.config.logLevel )
            this.localStorage.setLogLevel( application.config.logLevel )
          }
          if ( application && application.validation && application.validation.queueId  ) {
            return this.lbHttpService.getQueue( application.validation.queueId )
          } else {
            return of(false)
          }
        }),

        switchMap( ( queue: any ) => {
          if ( queue ) {
            this.localStorage.setSelectedQueue( queue )
            this.pwaService.changeTitle(applicationId)
            if ( application && application.validation && application.validation.endpointId  ) {
              return this.endpointService.httpGetEndpoint( application.validation.endpointId )
            } else {
              return of(true)
            }
          } else {
            return of(false)
          }
        }),
        switchMap( ( endpoint: any ) => {
          if ( endpoint ) {
            if ( typeof( endpoint ) === 'object' ) {
              this.localStorage.setSelectedEndpoint( endpoint )
            }
            return of( true )
          } else {
            return of(false)
          }
        }),
        catchError( (error) => {
          this.errorService.logError('Error while loading application with applicationId = ' + applicationId, error)
          return of( false )
        })
      )
    } else {
      applicationId = this.localStorage.getApplicationId()
      if ( !applicationId ) {
        return of( false )
      } else {
        this.pwaService.changeManifest( applicationId )
        return of( true )
      }
    }
  }

  setCustomApplication( application ) {
    this.localStorage.setApplicationId( application.applicationId )
    this.localStorage.setStorageValue(  ApplicationsService.CURRENT_APPLICATION_KEY , application )
    this.localStorage.setTheme( this.getTheme() )
    this.errorService.setDisplayError( application.applicationId )
    let ob: Observable<any>
    if ( application && application.validation && application.validation.queueId  ) {
      ob = this.lbHttpService.getQueue( application.validation.queueId )
    } else {
      ob = of(false)
    }

    return ob.pipe(
        switchMap( ( queue: any ) => {
          if ( queue ) {
            this.localStorage.setSelectedQueue( queue )
            if ( application && application.validation && application.validation.endpointId  ) {
              return this.endpointService.httpGetEndpoint( application.validation.endpointId )
            } else {
              return of(false)
            }
          } else {
            return of(false)
          }
        }),
        switchMap( ( endpoint: any ) => {
          if ( endpoint ) {
            this.localStorage.setSelectedEndpoint( endpoint )
            return of( true )
          } else {
            return of(false)
          }
        }),
        catchError( (error) => {
          this.errorService.logError('Error while loading application with applicationId = ' + application.applicationId, error)
          return of( false )
        })
      )
  }

  httpGetApplicationById( applicationId ) {
    let url = this.configService.getApplicationById
    url = url.replace( '{applicationId}', applicationId )

    return this.http.get(url).pipe(
        timeout(this.configService.defaultHttpTimeout),
        first(),
        retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
        catchError( (error) => {
          this.errorService.logError('Error while getting queue application from applicationId', error)
          return throwError( error )
        })
    )
  }
}
